// components/Client/ClientHistoryChanges/ClientLogModal.tsx
import React, { useMemo } from 'react';

import {
  Box,
  Chip,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  Tooltip,
} from '@material-ui/core';

import { formatDate } from '@/utils/utils';
import { getActionChipColor, getActionLabel } from '@/utils/utils';
import { modalDataSelector } from 'redux/app/app.selectors';
import { useAppSelector } from 'redux/hooks';
import { logsSelector } from 'redux/log/log.selectors';
import type { LogItem, LogEntry } from 'redux/log/log.types';

const isOldLogItem = (v: any): v is LogItem => v && typeof v === 'object' && '_id' in v;
const isNewLogEntry = (v: any): v is LogEntry => v && typeof v === 'object' && 'timestamp' in v && !('_id' in v);

const toChangesMap = (oldV?: Record<string, any>, newV?: Record<string, any>): Record<string, { old: any; new: any }> => {
  const oldObj = (oldV ?? {}) as Record<string, any>;
  const newObj = (newV ?? {}) as Record<string, any>;
  const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
  const out: Record<string, { old: any; new: any }> = {};
  keys.forEach((k) => {
    const ov = oldObj[k];
    const nv = newObj[k];
    // показываем только реальную разницу
    if (JSON.stringify(ov) !== JSON.stringify(nv)) out[k] = { old: ov, new: nv };
  });
  return out;
};

const FIELD_RU: Record<string, string> = {
  name: 'Имя',
  surname: 'Фамилия',
  patronymic: 'Отчество',
  phone: 'Телефон',
  email: 'Email',
  manager: 'Ответственный',
  brokerClientId: 'brokerClientId',
  type: 'Тип',
  updatedAt: 'Дата обновления',
  status: 'Статус',
  office: 'Офис',
};
const tField = (k: string) => FIELD_RU[k] || k;

const pretty = (v: any) => {
  if (v === null || v === undefined || v === '') {
    return <span style={{ color: 'var(--mui-palette-text-secondary)' }}>—</span>;
  }
  if (typeof v === 'boolean') return v ? 'Да' : 'Нет';
  if (typeof v === 'number') return String(v);
  if (v instanceof Date) return formatDate(v.toISOString());
  if (typeof v === 'string') {
    const iso = /^\d{4}-\d{2}-\d{2}T/.test(v);
    return iso ? formatDate(v) : v;
  }
  return (
    <pre
      style={{
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      }}
    >
      {JSON.stringify(v, null, 2)}
    </pre>
  );
};

const useStyles = makeStyles((theme) => ({
  headCell: {
    fontWeight: 600,
    background: theme.palette.type === 'light' ? '#f5f5f7' : '#1e1f22',
  },
  fieldCell: { width: '32%', fontWeight: 500 },
  valueCell: { width: '34%' },
  headerWrap: { padding: theme.spacing(2) },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  hint: { color: theme.palette.text.secondary },
  pill: { marginLeft: theme.spacing(1) },
}));

const ClientLogModal: React.FC = () => {
  const classes = useStyles();
  const id = useAppSelector<string>(modalDataSelector);
  const logs = useAppSelector(logsSelector);

  const log = useMemo(() => {
    const byId = logs.find((item: any) => '_id' in item && item._id === id);
    return byId ?? logs[0];
  }, [logs, id]);

  const header = useMemo(() => {
    if (!log) return null;

    const action = isOldLogItem(log) ? log.action : isNewLogEntry(log) ? log.action : '';
    const ts = isOldLogItem(log) ? log.timestamp : isNewLogEntry(log) ? log.timestamp : '';
    const entity = (log as any).entity || '';
    const entityId = (log as any).entity_id || '';

    const userName =
      (isOldLogItem(log) && typeof log.userName === 'string' && log.userName) || (log as any)?.user?.personalData
        ? `${(log as any).user?.personalData?.surname ?? ''} ${(log as any).user?.personalData?.name ?? ''}`.trim()
        : (log as any)?.user?._id || 'system';

    return { action, timestamp: ts, entity, entityId, userName };
  }, [log]);

  const changes = useMemo(() => {
    if (!log) return {};
    if (isOldLogItem(log)) return (log.changes ?? {}) as Record<string, { old: any; new: any }>;
    if (isNewLogEntry(log)) return toChangesMap(log.values_old as any, log.values_new as any);
    return {};
  }, [log]);

  if (!log) {
    return <Box p={2}>Нет данных по выбранной записи.</Box>;
  }

  const keys = Object.keys(changes);

  return (
    <Box>
      <Box className={classes.headerWrap}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md="auto">
            <Chip label={getActionLabel(header?.action || '')} color={getActionChipColor(header?.action || '')} size="small" />
          </Grid>
          <Grid item xs={12} md>
            <Typography variant="subtitle1">
              {header?.userName}{' '}
              {header?.entity ? (
                <span className={classes.hint}>
                  • {header.entity}
                  {header.entityId ? (
                    <>
                      {' '}
                      <Tooltip title="ID сущности">
                        <span className={`${classes.hint} ${classes.mono}`}>({header.entityId})</span>
                      </Tooltip>
                    </>
                  ) : null}
                </span>
              ) : null}
            </Typography>
            {header?.timestamp && (
              <Typography variant="body2" className={classes.hint}>
                {formatDate(header.timestamp)}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {keys.length === 0 ? (
        <Box p={2}>
          <Typography className={classes.hint}>Изменений нет.</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>Поле</TableCell>
              <TableCell className={classes.headCell} align="left">
                Было
              </TableCell>
              <TableCell className={classes.headCell} align="left">
                Стало
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((k) => (
              <TableRow key={k} hover>
                <TableCell className={classes.fieldCell}>{tField(k)}</TableCell>
                <TableCell className={classes.valueCell} align="left">
                  {pretty((changes as any)[k]?.old)}
                </TableCell>
                <TableCell className={classes.valueCell} align="left">
                  {pretty((changes as any)[k]?.new)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default React.memo(ClientLogModal);
